import { getItem, setItem } from "@app/helpers/storage.helper";
import { useEffect, useRef } from "react";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { useTranslation } from "react-i18next";
import { store, setError, setIsLoading } from "@app/store";
import { StorageKey, ApiStatusCodes, ApiErrorType } from "@app/constants";
import { useAuth, useCustomMsal, useForceLogout } from "@app/hooks";
import jsCookie from "js-cookie";
import { resetIsContinuousLoading } from "@app/helpers";

export type CustomAxiosRequestConfig = AxiosRequestConfig & {
  skipGlobalLoader?: boolean;
};

const TIMEOUT = 5 * 60 * 1000;

export const api = axios.create({
  timeout: TIMEOUT,
  baseURL: process.env.REACT_APP_API_HOST,
  headers: {
    "Content-Type": "application/json",
    platform: "WEBAPP",
  },
});

export const AuthInterceptor = () => {
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const { setTimer, isExpired } = useForceLogout();
  const { refreshToken } = useCustomMsal();

  const tRef = useRef(t);
  const langRef = useRef(i18n.language);
  const logoutRef = useRef(logout);
  const refreshTokenRef = useRef(refreshToken);
  const setTimerRef = useRef(setTimer);
  const isExpiredRef = useRef(isExpired);

  useEffect(() => {
    tRef.current = t;
    langRef.current = i18n.language;
    logoutRef.current = logout;
    refreshTokenRef.current = refreshToken;
    setTimerRef.current = setTimer;
    isExpiredRef.current = isExpired;
  }, [t, i18n.language, logout, refreshToken, setTimer, isExpired]);

  const isRefreshingRef = useRef(false);

  const failedQueueRef = useRef<
    {
      resolve: (token: string) => void;
      reject: (error: any) => void;
    }[]
  >([]);

  const processQueue = (error: any, token: string | null = null) => {
    failedQueueRef.current.forEach((prom) => {
      token ? prom.resolve(token) : prom.reject(error);
    });
    failedQueueRef.current = [];
  };

  useEffect(() => {
    const reqInterceptor = async (
      config: InternalAxiosRequestConfig,
    ): Promise<InternalAxiosRequestConfig> => {
      const { headers } = config;

      if (headers) {
        const uploadInfo = getItem(StorageKey.MEDIA);

        if (uploadInfo) {
          const { baseUrl, contentType } = uploadInfo;
          config.baseURL = baseUrl;
          config.timeout = undefined;

          headers
            .set("x-ms-blob-type", "BlockBlob")
            .set("Content-Type", contentType);
        } else {
          const expireResult = isExpiredRef.current();
          if (expireResult) {
            await logoutRef.current();
          }

          setTimerRef.current();

          const tokenString = jsCookie.get(StorageKey.AUTH_ACCESS_TOKEN);
          if (tokenString) {
            const tokens = JSON.parse(tokenString);
            headers
              .set("X-Access-Token", `Bearer ${tokens.accessToken}`)
              .set("x-lang", langRef.current);
          }
        }
      }

      if (!(config as CustomAxiosRequestConfig).skipGlobalLoader) {
        store.dispatch(setIsLoading(true));
      }
      return config;
    };

    const reqErrInterceptor = (error: AxiosError) => Promise.reject(error);

    const resInterceptor = (response: AxiosResponse) => {
      if (!(response.config as CustomAxiosRequestConfig).skipGlobalLoader) {
        if (!getItem(StorageKey.IS_CONTINUOUS_LOADING)) {
          store.dispatch(setIsLoading(false));
        }
      }
      return response;
    };

    const resErrInterceptor = async (error: any) => {
      const groupId = error?.response?.data?.error?.path?.split("/")[3];
      const errorStatus =
        error?.response?.status || error?.response?.statusCode || error.code;
      const errorType = error?.response?.data?.error?.errorType;
      const isDeleted = error?.response?.data?.error?.isDeleted;

      let content =
        error?.response?.data?.error?.localizedMessage ||
        tRef.current("unexpectedError");

      let title = "errNetwork.title";
      let btn = { title: tRef.current("ok"), style: "" };
      let onClick: (() => void) | null = null;

      const config = error.config;
      const expireResult = isExpiredRef.current();

      if (!(error.config as CustomAxiosRequestConfig)?.skipGlobalLoader) {
        store.dispatch(setIsLoading(false));
      }
      if (getItem(StorageKey.IS_CONTINUOUS_LOADING)) {
        setItem(StorageKey.IS_CONTINUOUS_LOADING, "");
      }

      switch (errorStatus) {
        case ApiStatusCodes.NOT_FOUND:
          if (isDeleted) {
            if (errorType === ApiErrorType.GROUP_NOT_FOUND) {
              onClick = () => (window.location.href = "/");
            }
            if (
              errorType === ApiErrorType.INSPECTION_FORM_NOT_FOUND &&
              groupId
            ) {
              onClick = () =>
                (window.location.href = `/groups/${groupId}/inspection-form`);
            }
            if (errorType === ApiErrorType.MACHINE_NOT_FOUND && groupId) {
              onClick = () =>
                (window.location.href = `/groups/${groupId}/machines`);
            }
          } else {
            if (errorType === ApiErrorType.USER_NOT_FOUND) {
              sessionStorage.setItem("signupProfile", "true");
              window.location.href = "/signup/profile";
              return Promise.reject(error);
            } else if (window.location.pathname !== "/") {
              onClick = () => (window.location.href = "/");
            }
          }
          break;

        case ApiStatusCodes.FORBIDDEN:
          if (window.location.pathname !== "/") {
            onClick = () => (window.location.href = "/");
          }
          break;

        case ApiStatusCodes.UNAUTHORIZED:
          console.log(
            "[DEBUG] UNAUTHORIZED:",
            error?.response?.data?.error?.message,
          );

          // Prevent refresh loop
          if (config._retry) {
            return Promise.reject(error);
          }
          config._retry = true;

          const expireResult = isExpiredRef.current();
          if (expireResult) {
            console.log("[DEBUG] Activity timeout expired, hard logout");
            logoutRef.current();
            return Promise.reject(new Error("Session timeout"));
          }

          if (error?.response?.data?.error?.message === "jwt expired") {
            if (expireResult) {
              logoutRef.current();
              return Promise.reject(new Error("Session expired"));
            }
            console.log("[DEBUG] JWT expired, starting refresh");

            if (isRefreshingRef.current) {
              console.log("[DEBUG] Refresh in progress, queuing");
              return new Promise((resolve, reject) => {
                failedQueueRef.current.push({
                  resolve: (token) => {
                    config.headers["X-Access-Token"] = `Bearer ${token}`;
                    resolve(api(config));
                  },
                  reject,
                });
              });
            }

            isRefreshingRef.current = true;
            try {
              const newToken = await refreshTokenRef.current();
              console.log(
                "[DEBUG] Refresh result:",
                newToken ? "Success" : "Failed",
              );

              if (newToken) {
                setTimerRef.current();
                jsCookie.set(
                  StorageKey.AUTH_ACCESS_TOKEN,
                  JSON.stringify({ accessToken: newToken }),
                );
                processQueue(null, newToken);
                config.headers["X-Access-Token"] = `Bearer ${newToken}`;
                resetIsContinuousLoading();
                return api(config);
              } else {
                throw new Error("Refresh token invalid");
              }
            } catch (err) {
              console.log("[DEBUG] Refresh failed:", err);
              processQueue(err, null);
              // Hard logout, no dialog
              logoutRef.current();
              return Promise.reject(new Error("Auth failed"));
            } finally {
              isRefreshingRef.current = false;
            }
          }

          // Non-expiry 401 → show dialog
          btn = { title: tRef.current("signOut"), style: "bg-red" };
          onClick = () => logoutRef.current();
          // return Promise.reject(error);
          break;

        case ApiStatusCodes.ERR_NETWORK:
          if (
            axios.isCancel(error) ||
            error.code === "ERR_CANCELED" ||
            error.message?.includes("aborted") ||
            error.message?.includes("NS_BINDING_ABORTED")
          ) {
            return Promise.reject(error);
          }

          title = tRef.current("errNetwork.title");
          content = tRef.current("errNetwork.content");
          break;
      }

      const notShownPopup = !!getItem(StorageKey.NOT_SHOWN_POPUP);
      if (
        notShownPopup &&
        ![ApiStatusCodes.UNAUTHORIZED, ApiStatusCodes.ERR_NETWORK].includes(
          errorStatus,
        )
      ) {
        title = "";
      }

      switch (errorType) {
        case ApiErrorType.GROUP_NOT_FOUND:
        case ApiErrorType.MACHINE_NOT_FOUND:
        case ApiErrorType.INSPECTION_FORM_NOT_FOUND:
        case ApiErrorType.MACHINE_REPORT_NOT_FOUND:
        case ApiErrorType.INSPECTION_FORM_Template_NOT_FOUND:
          if (!isDeleted) {
            window.location.href = "/404";
            return Promise.reject(error);
          }
          break;

        default:
          if (expireResult) return Promise.reject(error);
          break;
      }

      store.dispatch(
        setError({
          title,
          content,
          btn,
          onClick,
        }),
      );

      if (errorStatus === ApiStatusCodes.NOT_FOUND) {
        return Promise.resolve({
          data: {},
          status: ApiStatusCodes.NOT_FOUND,
          statusText: "Not Found",
          headers: {},
          config,
        } as AxiosResponse);
      }

      if (
        typeof errorStatus === "number" &&
        errorStatus === ApiStatusCodes.SERVER_ERROR
      ) {
        return Promise.resolve({
          data: {},
          status: ApiStatusCodes.SERVER_ERROR,
          statusText: "Server Error",
          headers: {},
          config,
        } as AxiosResponse);
      }

      if (
        typeof errorStatus === "number" &&
        errorStatus === ApiStatusCodes.SERVER_ERROR
      ) {
        return Promise.resolve({
          data: {},
          status: ApiStatusCodes.SERVER_ERROR,
          statusText: "Server Error",
          headers: {},
          config,
        } as AxiosResponse);
      }

      return Promise.reject(error);
    };

    const reqId = api.interceptors.request.use(
      reqInterceptor,
      reqErrInterceptor,
    );
    const resId = api.interceptors.response.use(
      resInterceptor,
      resErrInterceptor,
    );

    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, []);

  return null;
};
